import React from "react";
import { FetchBaseQueryError, skipToken } from "@reduxjs/toolkit/query/react";
import { Link, Navigate, Outlet, useLocation, useParams } from "react-router";
import { Page, PageNotFound, Tabs } from "@sscs/ui";
import {
  PACKAGE_ABOUT_ROUTE,
  PACKAGE_VERSIONS_ROUTE,
  STORE_PACKAGE_ID,
} from "Constants";
import { useIsRestoredId, useProjectsRoute } from "Hooks";
import { useGetPackageQuery } from "Services";

const TABS = [
  {
    id: "versions-tab",
    label: "Versions",
    to: PACKAGE_VERSIONS_ROUTE,
  },
  {
    id: "about-tab",
    label: "About",
    to: PACKAGE_ABOUT_ROUTE,
  },
] as const;

const getActiveTab = (pathPart: string): (typeof TABS)[number] => {
  return TABS.find((tab) => tab.to === pathPart) ?? TABS[0];
};

const PackageLayoutContent = ({
  packageId,
}: {
  packageId: string | undefined;
}): React.JSX.Element => {
  const { pathname } = useLocation();
  const { projectId } = useParams();
  const projectRoute = useProjectsRoute(projectId);
  const isRestored = useIsRestoredId(STORE_PACKAGE_ID, packageId);
  const activeTab = getActiveTab(
    pathname.split("/").pop() ?? PACKAGE_VERSIONS_ROUTE,
  );
  const {
    data: packageInfo,
    isFetching: isFetchingPackage,
    isLoading: isLoadingPackage,
    isError: isErrorPackage,
    error: packageError,
  } = useGetPackageQuery(
    packageId
      ? {
          packageId: packageId,
        }
      : skipToken,
  );

  if (isFetchingPackage || isLoadingPackage) {
    return <Page.Body status={{ status: "LOADING" }} />;
  } else if (
    isErrorPackage &&
    (packageError as FetchBaseQueryError)?.status === 404 &&
    isRestored
  ) {
    localStorage.removeItem(STORE_PACKAGE_ID);
    return <Navigate to={projectRoute} replace />;
  } else if (!packageInfo) {
    return (
      <PageNotFound
        title="We can't find that package"
        byline="Try selecting a package from the sidebar"
      />
    );
  }

  return (
    <>
      <Page.Tabs>
        <Tabs.Tabs value={activeTab.to}>
          {TABS.map((tabProps) => (
            <Tabs.Tab
              key={tabProps.to}
              id={tabProps.id}
              value={tabProps.to}
              label={tabProps.label}
              to={tabProps.to}
              component={Link}
            />
          ))}
        </Tabs.Tabs>
      </Page.Tabs>
      <Outlet />
    </>
  );
};

// Keyed by packageId so all internal state (e.g. the selected version, which is
// unique to one package) resets when the user navigates to a different package.
const PackageLayout = (): React.JSX.Element => {
  const { packageId } = useParams();
  return <PackageLayoutContent key={packageId} packageId={packageId} />;
};

export default PackageLayout;
